import './index.css';
import { useState } from 'react';
import Hero from './Hero';
import BotTicker from './BotTicker';
import FilterBar from './FilterBar';
import Statistics from './Statistics';
import SubscriptionsSection from './SubscriptionsSection';
import ChartsSection from './ChartsSection';
import BottomSection from './BottomSection';
import { ValueHideProvider } from './ValueHideContext';
import { FilterProvider } from './FilterContext';
import { SubscriptionSummary } from '../../../types/subscription';
import { Transaction } from '../types';
import { SummaryInformation } from '../types';
import LoadingIcons from 'react-loading-icons';
import NoSubscriptionsNotice from '../../../components/NoSubscriptionsNotice';
import type { HiddenSummary } from '../nearZero';

interface Props {
  /** The rows to RENDER — near-zero retired ones already removed. */
  subscriptions: SubscriptionSummary[];
  /**
   * Every subscription in scope, unfiltered. The headline and the portfolio chart are computed
   * from this: hiding is presentational and must never move "since inception".
   */
  allSubscriptions?: SubscriptionSummary[];
  /**
   * Retired near-zero rows removed from view by the user's threshold. They are still in every
   * total, so the screen states what is missing rather than letting the visible rows silently
   * fail to add up.
   */
  hidden?: HiddenSummary;
  totalValue: number;
  transactions: Transaction[];
  isLoading: boolean;
}

const Content = ({
  subscriptions,
  allSubscriptions,
  hidden,
  totalValue,
  transactions,
  isLoading,
}: Props) => {
  const [summaryInformation, setSummaryInformation] = useState<
    SummaryInformation | undefined
  >();

  if (!isLoading && !subscriptions?.length) {
    return <NoSubscriptionsNotice />;
  }

  if (isLoading && !subscriptions?.length) {
    return (
      <div className="overview-content-loading-container">
        <LoadingIcons.Grid width={100} />
      </div>
    );
  }

  return (
    <ValueHideProvider>
      <FilterProvider subscriptions={subscriptions}>
        <div className="overview-content-container">
          <div className="snap-section--hero">
            <Hero
              subscriptions={allSubscriptions ?? subscriptions}
              totalValue={totalValue}
              summaryInformation={summaryInformation}
              setSummaryInformation={setSummaryInformation}
            />
            <BotTicker subscriptions={subscriptions} />
          </div>

          {hidden && hidden.count > 0 ? (
            <div className="overview-content-hidden-note">
              {hidden.count} retired {hidden.count === 1 ? 'bot' : 'bots'}{' '}
              hidden
              {' · '}
              {hidden.profit >= 0 ? '+' : '−'}
              {Math.abs(hidden.profit).toFixed(2)} included in the totals
            </div>
          ) : undefined}

          <div className="snap-section--stats">
            <FilterBar />
            <Statistics
              subscriptions={subscriptions}
              transactions={transactions}
              totalValue={totalValue}
            />
          </div>

          <div className="snap-section--subs">
            <SubscriptionsSection
              subscriptions={subscriptions}
              totalValue={totalValue}
            />
          </div>

          <div className="snap-section--charts">
            <div className="charts-bottom-scroll">
              <ChartsSection
                subscriptions={subscriptions}
                totalValue={totalValue}
              />
              <BottomSection subscriptions={subscriptions} />
            </div>
          </div>
        </div>
      </FilterProvider>
    </ValueHideProvider>
  );
};

export default Content;
